import { Component, inject, Input, OnInit } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { POST_PAYMENT_TOKEN } from 'libs/mbs-ui-shared/src/lib/tokens/post-payment.token';
import { AlertComponent, ButtonComponent, InputLabelComponent, MbsPopupType, MbsSize, ModalService, SelectComponent } from 'mbs-ui-kit';
import { AddCompanyModalComponent } from 'mbs-ui-shared@components/add-company/add-company-modal.component';
import { WizardForm } from 'mbs-ui-shared@components/add-computer/add-computer.types';
import { getProductTilesConfig } from 'mbs-ui-shared@components/add-computer/steps/select-action/select-action.constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { Company, PermissionsEnum } from 'mbs-ui-shared@models';
import { BuildOsType } from 'mbs-ui-shared@models/build';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { EMPTY, from, Observable } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { AsyncPipe, CommonModule } from '@angular/common';

import { CanPipe } from 'ability';

import { ReactiveFormsModule } from '@angular/forms';

@UntilDestroy()
@Component({
  selector: 'add-computer-wizard-select-action-step',
  templateUrl: './select-action.component.html',
  styleUrls: ['../../add-computer.component.scss'],
  imports: [
    AlertComponent,
    AsyncPipe,
    ButtonComponent,
    CanPipe,
    CommonModule,
    InputLabelComponent,
    ReactiveFormsModule,
    SelectComponent,
    I18NextPipe
  ]
})
export class SelectActionComponent implements OnInit {
  private i18N = inject(I18NextPipe);
  private companiesFacade = inject(CompaniesFacade);
  private computersFacade = inject(ComputersFacade);
  private licensesFacade = inject(LicensesFacade);
  private modalService = inject(ModalService);
  private ability = inject(AbilityService);
  private auth = inject(AuthFacade);
  private helpService = inject(HelpService);
  isPostPayment$ = inject<Observable<boolean>>(POST_PAYMENT_TOKEN);

  public readonly permissionsEnum = PermissionsEnum;
  public readonly mbsPopupType = MbsPopupType;
  public readonly agentType = AgentType;
  public readonly size = MbsSize;

  public get isInternal(): boolean {
    return this.auth.isInternalAdmin;
  }

  public tilesConfig = getProductTilesConfig(this.i18N);
  public companies$ = toObservable(this.companiesFacade.data);
  public limitRMMCEExceeded$ = this.computersFacade.checkRmmCeLimit().pipe(
    map((limit) => limit.limitExceeded),
    shareReplay(1)
  );
  public agentTypeControl: FormControl;

  @Input() wizardForm: WizardForm;
  @Input() availableOsTypes: BuildOsType[];
  @Input() showCompanyUnsupportedAlert = false;

  public get RMMCEInfo(): string {
    return this.i18N
      .transform('shared:computers.addComputerWizard.actionStep.rmmCeInfo')
      .concat(this.ability.can('read', PermissionsEnum.HelpMarketing) ? '. ' : '');
  }

  ngOnInit(): void {
    this.agentTypeControl = this.wizardForm.get('agentType') as FormControl;

    if (!this.isTileAvailable(this.agentTypeControl.value)) {
      const fallback = [AgentType.Backup, AgentType.RMM, AgentType.RA].find((type) => this.isTileAvailable(type));
      this.agentTypeControl.patchValue(fallback ?? null);
    }

    this.companiesFacade.loadData();
  }

  private isTileAvailable(id: AgentType): boolean {
    switch (id) {
      case AgentType.RA:
        return this.ability.can('read', PermissionsEnum.ShowRemoteConnection) || this.isInternal;
      case AgentType.RMM:
        return this.ability.can('read', PermissionsEnum.Rmm) || this.isInternal;
      case AgentType.Backup:
        return this.ability.can('read', PermissionsEnum.Backup) || this.isInternal;
      default:
        return true;
    }
  }

  public onAddCompanyButtonClick() {
    from(this.modalService.openCustom(AddCompanyModalComponent))
      .pipe(catchError(() => EMPTY))
      .subscribe((company) => {
        this.wizardForm.get('companyId').patchValue((company as Company).id);
      });
  }

  public selectLicenseAfterRMMCE(): void {
    this.licensesFacade.openRMMCELimitModal().pipe(untilDestroyed(this)).subscribe();
  }

  public openRMMCEHelpPage(): void {
    window.open(this.helpService.getHelpLink('/remote-management/rmm-service/rmm-ce'), '_blank');
  }
}
